import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../sections/Navbar";
import Footer from "../sections/Footer";
import SEO from "../SEO";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import PlanCard from "./PlanCard";
import FreeTrialBanner from "./FreeTrialBanner";
import AuthModal from "./AuthModal";
import usePlanPurchase from "../../hooks/usePlanPurchase";
import { getPlans } from "../../lib/masterApi";
import { isFreePlan } from "../../lib/plans";
import { captureSessionFromSearch } from "../../lib/planSession";

const PREFERRED_GROUP_ORDER = ["channel based", "unlimited"];
const getGroupPriority = (name) => {
  const idx = PREFERRED_GROUP_ORDER.indexOf((name || "").trim().toLowerCase());
  return idx === -1 ? 999 : idx;
};

// 1 / 2 / 4 cards per row, accounting for the 1.25rem (gap-5) gutters.
const CARD_WIDTH = "w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(25%-0.9375rem)]";

export default function PlansPage() {
  const { search, pathname } = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [group, setGroup] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const purchase = usePlanPurchase();

  // Pick up ?token= / ?returnUrl= if the panel linked straight to the catalog.
  useEffect(() => {
    if (captureSessionFromSearch(search)) window.history.replaceState(null, "", pathname);
  }, [search, pathname]);

  useEffect(() => {
    let live = true;
    setError(false);
    getPlans()
      .then((d) => live && setData(d))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [attempt]);

  // The free / zero-price plan is promoted in a banner instead of the card list.
  const freePlan = useMemo(() => (data?.plans || []).find(isFreePlan) || null, [data]);

  const plans = useMemo(() => {
    const list = (data?.plans || []).filter((p) => !isFreePlan(p));
    list.sort((a, b) => {
      const priceA = Number(a.price) || 0;
      const priceB = Number(b.price) || 0;
      if (priceA !== priceB) return priceA - priceB;
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [data]);

  const groups = useMemo(() => {
    const withPlans = new Set(plans.map((p) => p.groupName).filter(Boolean));
    const fromApi = (data?.groups || []).map((g) => g.groupName).filter((g) => withPlans.has(g));
    const rawGroups = fromApi.length ? fromApi : [...withPlans];

    return [...rawGroups].sort((a, b) => {
      const pA = getGroupPriority(a);
      const pB = getGroupPriority(b);
      if (pA !== pB) return pA - pB;
      return a.localeCompare(b);
    });
  }, [data, plans]);

  const active = group && groups.includes(group) ? group : groups[0];
  const visible = groups.length ? plans.filter((p) => p.groupName === active) : plans;

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="plans-page">
      <SEO title="Master Plans | MagicBolt AI" description="Choose a MagicBolt AI agency plan." path="/plans" noindex />
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">Choose your plan</h1>
          <p className="mt-2 text-slate-600">Pick a plan, pay securely with Razorpay and start instantly.</p>
        </div>

        {freePlan && (
          <FreeTrialBanner
            plan={freePlan}
            onStart={purchase.purchase}
            busy={purchase.busyPlanId === freePlan.id}
            disabled={Boolean(purchase.busyPlanId)}
          />
        )}

        {groups.length > 0 && (
          <div className="mt-8 flex justify-center" role="tablist" data-testid="plans-tabs">
            <div className="inline-flex rounded-full border border-[#E6E9F1] bg-white p-1">
              {groups.map((g) => (
                <button
                  key={g}
                  role="tab"
                  aria-selected={g === active}
                  onClick={() => setGroup(g)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                    g === active ? "bg-[#FF6B00] text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Flex instead of grid so a row with fewer than 4 plans stays centered. */}
        <div className="mt-10 flex flex-wrap justify-center gap-5">
          {!data && !error &&
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className={`h-96 rounded-xl ${CARD_WIDTH}`} />)}
          {visible.map((plan) => (
            <div key={plan.id} className={CARD_WIDTH}>
              <PlanCard
                plan={plan}
                onPurchase={purchase.purchase}
                busy={purchase.busyPlanId === plan.id}
                disabled={Boolean(purchase.busyPlanId)}
              />
            </div>
          ))}
        </div>

        {error && (
          <div className="mt-10 text-center" data-testid="plans-error">
            <p className="text-slate-700">We couldn't load the plans. Please try again.</p>
            <Button className="mt-3" variant="outline" onClick={() => setAttempt((a) => a + 1)}>
              Retry
            </Button>
          </div>
        )}
        {data && visible.length === 0 && <p className="mt-10 text-center text-slate-600">No plans available right now.</p>}
      </main>
      <Footer />
      <AuthModal
        open={Boolean(purchase.authPlan)}
        planName={purchase.authPlan?.name}
        isFree={Boolean(purchase.authPlan && isFreePlan(purchase.authPlan))}
        onClose={purchase.closeAuth}
        onAuthenticated={purchase.onAuthenticated}
      />
    </div>
  );
}
